import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Eye, EyeOff, Lock } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';
import { Player, GameState } from '../types';

interface RoleRevealPageProps {
  gameState: GameState;
  currentPlayer: Player;
  players: Player[];
  onBack: () => void;
  onFlipCard: (playerId: string, reveal: boolean) => void;
  onContinueToVoting: () => void;
}

export const RoleRevealPage: React.FC<RoleRevealPageProps> = ({
  gameState,
  currentPlayer,
  players,
  onBack,
  onFlipCard,
  onContinueToVoting,
}) => {
  const { t } = useTranslation();
  
  const cardState = gameState.cardFlipStates[currentPlayer.id] || { isRevealed: false, flipCount: 0 };
  const isImpostor = gameState.roles[currentPlayer.id] === 'impostor';
  const isHost = currentPlayer.isHost;
  const isLocked = cardState.flipCount >= 2;

  const handleCardClick = () => {
    if (isLocked) return;
    
    const newRevealState = !cardState.isRevealed;
    onFlipCard(currentPlayer.id, newRevealState);
  };

  const getCategoryEmoji = (category: string) => {
    const emojis: Record<string, string> = {
      animales: '🐾',
      comida: '🍔',
      objetos: '📦',
      películas: '🎬',
      lugares: '🌍',
      profesiones: '💼',
    };
    return emojis[category] || '📋';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 pointer-events-none"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Volver
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            🃏 Tu Rol Secreto
          </h1>
          <p className="text-xl text-white/80">
            Toca tu carta para revelar tu rol
          </p>
        </div>

        {/* Instructions */}
        <Card className="mb-6">
          <div className="text-center">
            <h2 className="text-xl font-bold text-white mb-3">📋 Instrucciones</h2>
            <div className="space-y-2 text-white/80">
              <p>• Puedes voltear tu carta <strong>una vez</strong> para ver tu rol</p>
              <p>• Puedes desvoltearla <strong>una vez más</strong></p>
              <p>• Después de eso, la carta queda <strong>bloqueada 🔒</strong></p>
              <p>• No muestres tu pantalla a otros jugadores</p>
            </div>
          </div>
        </Card>

        {/* Category Info */}
        <Card className="mb-6 border-2 border-primary-400 bg-primary-500/10">
          <div className="text-center">
            <div className="text-4xl mb-2">{getCategoryEmoji(gameState.category)}</div>
            <h2 className="text-lg font-bold text-white">
              Categoría: <span className="capitalize">{gameState.category}</span>
            </h2>
          </div>
        </Card>

        {/* Card */}
        <div className="mb-8 flex justify-center">
          <div 
            className="relative w-full max-w-md cursor-pointer perspective-1000"
            onClick={handleCardClick}
          >
            <div 
              className={`relative w-full transition-transform duration-500 transform-style-3d ${
                cardState.isRevealed ? 'rotate-y-180' : ''
              }`}
              style={{
                transformStyle: 'preserve-3d',
                transform: cardState.isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)',
              }}
            >
              {/* Front (Hidden) */}
              <div 
                className="absolute w-full backface-hidden"
                style={{ backfaceVisibility: 'hidden' }}
              >
                <div className={`p-12 rounded-3xl border-4 shadow-2xl ${
                  isLocked
                    ? 'border-gray-500 bg-gray-600/30'
                    : 'border-primary-500 bg-gradient-to-br from-primary-600 to-primary-800 hover:scale-105'
                } transition-all duration-300 min-h-[400px] flex flex-col items-center justify-center`}>
                  <div className="text-8xl mb-6">🎭</div>
                  <h2 className="text-3xl font-bold text-white mb-4">{currentPlayer.name}</h2>
                  <p className="text-white/80 text-center mb-4">
                    {isLocked ? 'Carta bloqueada' : 'Toca para revelar'}
                  </p>
                  {isLocked && <Lock className="w-12 h-12 text-white/60" />}
                  {!isLocked && (
                    <Eye className="w-12 h-12 text-white/60 animate-pulse" />
                  )}
                  <div className="mt-4 text-white/60 text-sm">
                    Volteos: {cardState.flipCount}/2
                  </div>
                </div>
              </div>

              {/* Back (Revealed) */}
              <div 
                className="w-full backface-hidden"
                style={{ 
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                }}
              >
                <div className={`p-12 rounded-3xl border-4 shadow-2xl min-h-[400px] flex flex-col items-center justify-center ${
                  isImpostor
                    ? 'border-accent-500 bg-gradient-to-br from-accent-600 to-red-800'
                    : 'border-success-500 bg-gradient-to-br from-success-600 to-green-800'
                }`}>
                  <div className="text-8xl mb-6">
                    {isImpostor ? '😈' : '✅'}
                  </div>
                  <h2 className="text-4xl font-bold text-white mb-4">
                    {isImpostor ? '¡IMPOSTOR!' : '¡JUGADOR!'}
                  </h2>
                  {!isImpostor && (
                    <div className="text-center">
                      <p className="text-white/80 text-xl mb-3">Tu palabra es:</p>
                      <p className="text-5xl font-bold text-white uppercase tracking-wider">
                        {gameState.word}
                      </p>
                    </div>
                  )}
                  {isImpostor && (
                    <div className="text-center">
                      <p className="text-white/80 text-xl">
                        No sabes cuál es la palabra.
                      </p>
                      <p className="text-white/80 text-xl">
                        ¡Descúbrela sin que te descubran!
                      </p>
                    </div>
                  )}
                  <div className="mt-6 flex items-center gap-2 text-white/60">
                    <EyeOff className="w-5 h-5" />
                    <span className="text-sm">Toca para ocultar</span>
                  </div>
                  <div className="mt-2 text-white/60 text-sm">
                    Volteos: {cardState.flipCount}/2
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lock Warning */}
        {cardState.flipCount === 1 && (
          <Card className="mb-6 border-2 border-accent-400 bg-accent-500/10">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-accent-400" />
              <p className="text-white/90">
                ⚠️ Cuidado: después de voltear una vez más, tu carta se bloqueará
              </p>
            </div>
          </Card>
        )}

        {/* Host Continue Button */}
        {isHost && (
          <div className="flex justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={onContinueToVoting}
              icon={<ArrowLeft className="w-5 h-5" />}
            >
              Continuar a Votación
            </Button>
          </div>
        )}

        {!isHost && (
          <Card className="text-center">
            <p className="text-white/80">
              Recuerda tu rol. Esperando a que el anfitrión continúe...
            </p>
          </Card>
        )}
      </div>

      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </div>
  );
};
